/** @jsxImportSource @emotion/react */
import MUTypography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@emotion/react';
import CheckBox from '@CheckBox';
import IconTileCheckBox from '@IconTileCheckBox';
import ListLayout from '@ListLayout';
import TileLayout from '@TileLayout';
import { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  clearConstraintsValidations,
  selectOptionAndUpdateValue,
  setComponentValidationStatus,
} from '../../Lib/redux/slices/components';
import QuestionText from '../QuestionText/QuestionText';
import {
  isIconForEveryOption,
  swapS3DomainWithPublicAssetsDomain,
  getIconHeight,
} from '../__helpers__/component.helpers';
import { handleAnswerQuestionSideEffectsSaga } from '../../Hooks/actionCreators';
import { setApplicationError } from '../../Lib/redux/slices/errors';
import ApplicationErrors from '../../Data/ApplicationErrors';
import { renderMustache } from '../../Helpers/mustache';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';

/**
 * SupplierMatch Component
 *
 * This component renders a set of suppliers so that a customer can choose where their information will be distributed.
 * The list of suppliers shown will be generated dynamically based on earlier customer inputs.
 *
 */
const SupplierMatch = ({ componentId, subheading, iFrameId, formId }) => {
  const dispatch = useDispatch();
  const [s3EnvDomain, setS3EnvDomain] = useState(
    process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL
  );
  const components = useSelector((store) => store.components);
  const component = components[`component_${componentId}`];
  const { options, selectedOptions, areOptionLabelsHidden } = component.input;
  const areOptionsAvailable = options && options.length > 0;
  const { text, forceListLayout } = component.component;
  const additionalSettings = component?.component?.additionalSettings || {};
  const {
    user_deselected_a_supplier_message: userDeselectedASupplierMessage,
    disable_deselection: disableDeselection,
  } = additionalSettings;

  const theme = useTheme();
  const allOptionsHaveIcons = isIconForEveryOption(options);
  const isTiled = allOptionsHaveIcons && !forceListLayout;
  const Layout = isTiled ? TileLayout : ListLayout;
  const shouldShowLabel = !areOptionLabelsHidden || !allOptionsHaveIcons;
  const isChecked = (option) => selectedOptions?.includes(String(option.id));

  const checkboxExperimentName = useGetExperimentName({
    componentId,
    componentName: 'CheckBox',
  });
  const iconCheckboxExperimentName = useGetExperimentName({
    componentId,
    componentName: 'IconTileCheckBox',
  });
  const layoutExperimentName = useGetExperimentName({
    componentId,
    componentName: isTiled ? 'TileLayout' : 'ListLayout',
  });

  const couldSelectMoreOptions =
    selectedOptions?.length !== options?.length && selectedOptions.length > 0;

  const columnCount = component?.component?.additionalSettings?.column_count;

  const handleClick = ({ id, selected }) => {
    batch(() => {
      dispatch(clearConstraintsValidations({ componentId }));
      dispatch(
        selectOptionAndUpdateValue({
          componentId,
          optionId: id,
          selected,
        })
      );
      dispatch(
        setComponentValidationStatus({
          componentId,
          iFrameId,
          formId,
        })
      );
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
    });
  };

  useEffect(() => {
    if (s3EnvDomain !== process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL) {
      setS3EnvDomain(process.env.NEXT_PUBLIC_CHAMELEON_CLIENT_ASSETS_URL);
    }
  }, [s3EnvDomain]);

  useEffect(() => {
    if (!areOptionsAvailable) {
      // If it has not been possible to match with any suppliers display an error page and increment a metric.
      dispatch(
        setApplicationError({
          applicationError: ApplicationErrors.NO_MATCHES_AVAILABLE,
        })
      );
    }
  }, [areOptionsAvailable]);

  if (!areOptionsAvailable) {
    return null;
  }

  return (
    <>
      <QuestionText
        heading={renderMustache({
          template: text,
          components,
          currentComponent: component,
          iFrameId,
          formId,
        })}
        subheading={renderMustache({
          template: subheading,
          components,
          currentComponent: component,
          iFrameId,
          formId,
        })}
      />
      <Box
        marginTop={theme.mvf.spacing.medium}
        data-testid="supplierMatchContainer"
      >
        <Layout
          experimentName={layoutExperimentName}
          columns={isTiled ? undefined : columnCount}
        >
          {options.map((option) => {
            const { value, id } = option;
            const optionIdString = String(id);
            const strippedUrl = swapS3DomainWithPublicAssetsDomain(
              option.icon_url,
              process.env.NEXT_PUBLIC_ASSETS_URL
            );
            const Icon = allOptionsHaveIcons ? (
              <img
                style={{
                  maxWidth: '100%',
                  height: getIconHeight(isTiled, columnCount),
                }}
                src={strippedUrl}
                alt="optionImage"
              />
            ) : undefined;

            return isTiled ? (
              <IconTileCheckBox
                key={optionIdString}
                id={optionIdString}
                selected={isChecked(option)}
                onClick={handleClick}
                Icon={Icon}
                text={value}
                showText={shouldShowLabel}
                hideCheckbox={disableDeselection}
                experimentName={iconCheckboxExperimentName}
                disabled={disableDeselection}
              />
            ) : (
              <CheckBox
                Icon={Icon}
                id={optionIdString}
                key={optionIdString}
                onClick={handleClick}
                selected={isChecked(option)}
                textAlign="center"
                text={value}
                showText={shouldShowLabel}
                hideCheckbox={disableDeselection}
                experimentName={checkboxExperimentName}
                disabled={disableDeselection}
                smallIcon={columnCount > 1 && !isTiled}
              />
            );
          })}
        </Layout>
        {userDeselectedASupplierMessage && couldSelectMoreOptions && (
          <div
            style={{ marginTop: theme.mvf.spacing.medium }}
            data-testid="userDeselectedASupplierMessage"
          >
            <MUTypography
              style={{
                color: theme.mvf.palette.status.error,
              }}
              variant="error"
            >
              {userDeselectedASupplierMessage}
            </MUTypography>
          </div>
        )}
      </Box>
    </>
  );
};

export default SupplierMatch;
